import React from 'react'
import PropTypes from 'prop-types'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Box } from '@chatterbug/aaron'

import { t } from 'src/lib/i18n-react'
import {
  getLocale,
  getTimezone,
  shouldTimeBeIn24HourTime,
} from 'src/config/globals'

const getViewForScreenSize = () => {
  return window.innerWidth < 768 ? 'timeGridDay' : 'timeGridWeek'
}

const PLUGINS = [timeGridPlugin]

const BUTTON_ICONS = {
  prev: 'left-single-arrow',
  next: 'right-single-arrow',
}

const Calendar = ({
  defaultDate,
  firstDay,
  events,
  editable,
  selectable,
  selectHelper,
  onEventClick,
  onEventCreate,
  onEventUpdate,
  onEventRender,
  onStartFetch,
  onFinishFetch,
  calendarRef,
  filtersApplied,
}) => {
  const handleEventClick = (data) => {
    onEventClick(data)
  }

  const handleEventRender = (args) => {
    const { event, el } = args
    const type = event.extendedProps?.event_type
    const elTitle = el.querySelector('.fc-title')
    if (type === 'appointment-live' || type === 'preferred') {
      el.classList.add(
        `clevel__calendar__booking_${event.extendedProps.event_type}`
      )
      elTitle.innerHTML = event.title
    } else {
      el.classList.add(
        `clevel__calendar__booking_${event.extendedProps.event_type}`
      )
      if (event.extendedProps?.available === 1) {
        elTitle.innerHTML = filtersApplied ? event.title : t('only 1 left')
      } else {
        elTitle.innerHTML = filtersApplied
          ? t('%{smart_count} tutor |||| %{smart_count} tutors', {
              smart_count: event.extendedProps?.available,
            })
          : ''
      }
    }
  }

  const CALENDAR_FORMAT = {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short',
    hour12: !shouldTimeBeIn24HourTime(),
  }

  return (
    <Box
      gridGap="1x"
      className="calendar"
      bg="white100"
      borderRadius="1x"
      boxShadow="raised"
      p="2x"
    >
      <FullCalendar
        ref={calendarRef}
        height={800}
        timeZone={getTimezone()}
        defaultView={getViewForScreenSize()}
        defaultDate={defaultDate || new Date()}
        businessHours={{
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          start: '8:00',
          end: '18:00',
        }}
        eventTimeFormat={CALENDAR_FORMAT}
        firstDay={firstDay || 1}
        buttonIcons={BUTTON_ICONS}
        plugins={PLUGINS}
        editable={editable}
        selectable={selectable}
        selectHelper={selectHelper}
        allDaySlot={false}
        slotDuration="00:30:00"
        snapDuration="00:15:00"
        locale={getLocale()}
        slotLabelFormat={CALENDAR_FORMAT}
        events={events}
        nowIndicator={true}
        eventClick={handleEventClick}
        eventDrop={onEventUpdate}
        eventResize={onEventUpdate}
        eventRender={onEventRender || handleEventRender}
        select={onEventCreate}
        loading={(isLoading) => {
          isLoading ? onStartFetch?.() : onFinishFetch?.()
        }}
        windowResize={() => {
          const newView = getViewForScreenSize()
          const calendarApi = calendarRef.current?.getApi?.()

          if (calendarApi) {
            calendarApi.changeView(newView)
          }
        }}
      />
    </Box>
  )
}

Calendar.propTypes = {
  defaultDate: PropTypes.object,
  firstDay: PropTypes.number,
  events: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }),
  editable: PropTypes.bool,
  selectable: PropTypes.bool,
  selectHelper: PropTypes.bool,
  onEventClick: PropTypes.func,
  onEventCreate: PropTypes.func,
  onEventUpdate: PropTypes.func,
  onEventRender: PropTypes.func,
  onStartFetch: PropTypes.func,
  onFinishFetch: PropTypes.func,
  calendarRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  filtersApplied: PropTypes.bool,
}

export default Calendar
